import { useCallback, useEffect, useState } from "react";
import { Navigate, Route, Routes, useNavigationType } from "react-router-dom";
import { isRoundOpen } from "@ptp/contracts";
import { fetchPublicConfig, startSession } from "./lib/api.js";
import { env } from "./lib/env.js";
import { Launch } from "./routes/Launch.js";
import { Done } from "./routes/Done.js";
import { UniverseCanvas } from "./background/UniverseCanvas.js";
import { BackgroundDimProvider } from "./background/BackgroundDim.js";
import styles from "./App.module.css";

/** `limitReached`: this device can't take part. `roundClosed`: the pattern has started. */
type SessionStatus = "pending" | "open" | "limitReached" | "roundClosed";

export function App() {
  const [session, setSession] = useState<SessionStatus>("pending");

  const checkSession = useCallback(() => {
    // Establishes the signed session cookie up front, so the first submission is
    // already attributable to this device. A failure here is not fatal: the
    // X-Client-Id header carries the same role.
    const limitReached = startSession()
      .then((s) => s.limitReached)
      .catch(() => false);
    // A failed check leaves the launch open; a release would still be refused.
    const roundClosed = fetchPublicConfig()
      .then((config) => !isRoundOpen(config.stage))
      .catch(() => false);
    void Promise.all([limitReached, roundClosed]).then(([limit, closed]) =>
      setSession(closed ? "roundClosed" : limit ? "limitReached" : "open"),
    );
  }, []);

  useEffect(checkSession, [checkSession]);

  // A reset frees every phone, so the session is checked again before the launch.
  const onRoundReset = useCallback(() => {
    setSession("pending");
    checkSession();
  }, [checkSession]);

  return (
    <>
      {/* Outside the routes, so the field runs on unbroken from screen to screen. */}
      <UniverseCanvas />
      <BackgroundDimProvider>
        <div className={styles.screens}>
          {session === "pending" ? null : <Screens session={session} onRoundReset={onRoundReset} />}
        </div>
        <div className={styles.rotateHint} aria-live="polite">
          <h1>Please turn your phone upright.</h1>
        </div>
      </BackgroundDimProvider>
    </>
  );
}

function Screens({
  session,
  onRoundReset,
}: {
  session: Exclude<SessionStatus, "pending">;
  onRoundReset: () => void;
}) {
  // POP: the page was (re)loaded or reached via back/forward, not by the app itself.
  const navigationType = useNavigationType();
  return (
    <Routes>
      <Route
        path="/"
        element={
          session === "open" || env.tweak ? (
            <Launch />
          ) : (
            // Done opens on its final screen right away rather than after its first poll.
            <Navigate to="/done" replace state={{ roundClosed: session === "roundClosed" }} />
          )
        }
      />
      <Route
        path="/done"
        element={
          // A phone that can still take part (e.g. after a reset) belongs at the launch.
          session === "open" && navigationType === "POP" ? (
            <Navigate to="/" replace />
          ) : (
            <Done onRoundReset={onRoundReset} />
          )
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
